import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { BookingDto, BookingService } from '../services/booking.service';
import { ProductDto, ProductService } from '../services/product.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { MyCartEstimate, MyCartService } from '../services/my-cart.service';
import { InvoiceDto, InvoiceService } from '../services/invoice.service';
import { HelcimPayModalService } from '../services/helcimpay-modal.service';
import { TestPaymentService } from '../services/test-payment.service';
import { environment } from '../../environments/environment';

interface CartLine {
  productId: number;
  name: string;
  sku: string;
  unitPrice: number;
  quantity: number;
}

@Component({
  selector: 'app-my-cart-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">Home</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--sky">🛒</div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">My Cart</h1>
          <p class="page-sub">Pick products, build an estimate, and checkout by booking.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="layout">
            <div class="left-column">
              <div class="card">
                <div class="card-hd">
                  <span class="card-title">Bookings</span>
                  <span class="card-count">{{ bookings.length }}</span>
                </div>

                @if (isLoadingBookings) {
                  <div class="card-empty">Loading bookings...</div>
                } @else if (bookingError) {
                  <div class="card-empty">{{ bookingError }}</div>
                } @else {
                  <div class="list">
                    @for (booking of bookings; track booking.id) {
                      <button class="row"
                              [class.row--selected]="selectedBookingId() === booking.id"
                              (click)="selectBooking(booking)">
                        <span class="row-name">{{ booking.name }}</span>
                        <span class="row-meta">{{ formatDateTime(booking.scheduledDateTime) }}</span>
                      </button>
                    } @empty {
                      <div class="card-empty">No bookings found.</div>
                    }
                  </div>
                }
              </div>
              <div class="card">
                <div class="invoice-filter-bar">
                  <input
                    class="invoice-filter-input"
                    type="text"
                    placeholder="Filter by date, status, type, or invoice number"
                    aria-label="Filter invoices"
                    [(ngModel)]="invoiceFilterText" />
                  @if (invoiceFilterText.trim()) {
                    <button class="invoice-filter-clear-btn" type="button" (click)="clearInvoiceFilter()">Clear</button>
                  }
                </div>
                <div class="card-hd">
                  <span class="card-title">Orders / Invoices</span>
                  <div class="invoice-count-actions">
                    <span class="card-count">{{ filteredInvoices().length }}</span>
                    <button
                      class="line-items-add-btn"
                      type="button"
                      aria-label="Create Invoice"
                      [disabled]="isLoadingInvoices || isCreatingInvoice || !organizationId"
                      (click)="createInvoice()">+</button>
                  </div>
                </div>

                @if (isLoadingInvoices) {
                  <div class="card-empty">Loading invoices...</div>
                } @else if (invoiceError) {
                  <div class="card-empty">{{ invoiceError }}</div>
                } @else {
                  <div class="list">
                    @for (invoice of filteredInvoices(); track invoice.id) {
                      <button class="row" (click)="selectInvoice(invoice)">
                        <span class="row-name">{{ invoice.invoiceNumber || 'Pending Invoice Number' }}</span>
                        <span class="row-meta">
                          {{ formatDateTime(invoice.dateIssued || invoice.dateCreated || '') }} ·
                          {{ formatCurrency(invoice.amountDue) }} ·
                          {{ invoice.status || 'Status Unknown' }}
                        </span>
                      </button>
                    } @empty {
                      <div class="card-empty">{{ invoiceEmptyMessage() }}</div>
                    }
                  </div>
                }
              </div>
            </div>

            <div class="card cart-card">
              <div class="card-hd">
                <span class="card-title">Cart</span>
                <span class="estimate-pill">{{ estimateType }}</span>
              </div>

              <div class="cart-header-panel">
                <div class="cart-header-grid">
                  <div class="cart-header-item">
                    <span>Booking</span>
                    <strong>{{ selectedBookingSummary() }}</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Type</span>
                    <strong>{{ estimateType }}</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Currency</span>
                    <strong>USD</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Invoice Number</span>
                    <strong>{{ estimateInvoiceNumber || 'Pending' }}</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Status</span>
                    <strong>{{ displayInvoiceStatus() }}</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Pickup Date</span>
                    <strong>{{ selectedPickupDate() }}</strong>
                  </div>
                  <div class="cart-header-item">
                    <span>Pickup Name</span>
                    <strong>{{ selectedPickupName() }}</strong>
                  </div>
                </div>

                <div class="cart-contact-panel">
                  <div class="cart-contact-title">Customer Contact Information</div>
                  <div class="cart-contact-grid">
                    <div class="cart-header-item">
                      <span>Contact Name</span>
                      <strong>{{ customerContactName() }}</strong>
                    </div>
                    <div class="cart-header-item">
                      <span>Contact Email</span>
                      <strong>{{ customerContactEmail() }}</strong>
                    </div>
                    <div class="cart-header-item">
                      <span>Contact Phone</span>
                      <strong>{{ customerContactPhone() }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="cart-list">
                <div class="line-items-header-row">
                  <div class="line-items-col-item">
                    <div class="line-items-col-item-header">
                      <span>Item</span>
                      <button
                        class="line-items-add-btn"
                        type="button"
                        aria-label="Open Product List"
                        [disabled]="isLoadingProducts || !isEstimate()"
                        (click)="openProductListPopup()">+</button>
                    </div>
                  </div>
                  <div class="line-items-col-qty">Qty</div>
                  <div class="line-items-col-price">Price</div>
                  <div class="line-items-col-total">Total</div>
                  <div class="line-items-col-remove"></div>
                </div>
                @for (line of cartLines(); track line.productId) {
                  <div class="line-items-data-row">
                    <div class="line-items-col-item">
                      <div class="line-item-name">{{ line.name }}</div>
                      <div class="line-item-meta">SKU: {{ line.sku }}</div>
                    </div>
                    <div class="line-items-col-qty qty-controls">
                      <button class="qty-btn" [disabled]="!isEstimate()" (click)="decrementQuantity(line.productId)">-</button>
                      <span class="qty">{{ line.quantity }}</span>
                      <button class="qty-btn" [disabled]="!isEstimate()" (click)="incrementQuantity(line.productId)">+</button>
                    </div>
                    <div class="line-items-col-price">{{ formatCurrency(line.unitPrice) }}</div>
                    <div class="line-items-col-total">{{ formatCurrency(lineTotal(line)) }}</div>
                    <div class="line-items-col-remove">
                      <button class="remove-btn" [disabled]="!isEstimate()" (click)="removeLine(line.productId)">Remove</button>
                    </div>
                  </div>
                } @empty {
                  <div class="card-empty">No products in cart.</div>
                }
              </div>

              @if (isProductListPopupOpen) {
                <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                  <div style="width:min(620px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                      <div style="font-weight:700">Product List</div>
                      <button class="btn" type="button" (click)="closeProductListPopup()">Close</button>
                    </div>
                    <div style="max-height:380px;overflow:auto;border:1px solid color-mix(in srgb, var(--color-border) 45%, transparent);border-radius:.55rem">
                      @if (isLoadingProducts) {
                        <div class="card-empty">Loading products...</div>
                      } @else if (productError) {
                        <div class="card-empty">{{ productError }}</div>
                      } @else {
                        <div class="list">
                          @for (product of products; track product.id) {
                            <div class="product-row">
                              <div class="product-main">
                                <span class="row-name">{{ product.name }}</span>
                                <span class="row-meta">{{ product.sku }}</span>
                              </div>
                              <div class="product-side">
                                <span class="price">{{ formatCurrency(product.defaultPrice ?? 0) }}</span>
                                <button class="btn" [disabled]="!isEstimate()" (click)="addProductFromPopup(product)">Add</button>
                              </div>
                            </div>
                          } @empty {
                            <div class="card-empty">No products found.</div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              <div class="cart-footer">
                <div class="totals-checkout-title">Totals & Checkout</div>
                <div class="selected-booking">Booking: {{ selectedBookingSummary() }}</div>
                <div class="totals-box">
                  <div class="totals-line"><span>Subtotal</span><strong>{{ formatCurrency(displaySubtotal()) }}</strong></div>
                  <div class="totals-line"><span>Discount</span><strong>{{ displayAdjustmentText(displayDiscount()) }}</strong></div>
                  <div class="totals-line"><span>Tax</span><strong>{{ displayAdjustmentText(displayTax()) }}</strong></div>
                  <div class="totals-line"><span>Shipping</span><strong>{{ displayAdjustmentText(displayShipping()) }}</strong></div>
                  <div class="totals-line totals-line--grand"><span>Amount Due</span><strong>{{ formatCurrency(displayAmountDue()) }}</strong></div>
                  <div class="totals-line"><span>Amount Paid</span><strong>{{ displayAmountPaidText() }}</strong></div>
                </div>
                <div class="actions">
                  <button class="btn" (click)="saveEstimate()" [disabled]="isQuoteOrLater() || saveEstimateValidationErrors().length > 0">Save Estimate</button>
                  <button class="btn" (click)="saveQuote()" [disabled]="!isEstimate()">Save Quote</button>
                  <button class="btn" (click)="changeQuote()" [disabled]="!isQuote()">Change Quote</button>
                  <button class="btn btn--primary" (click)="checkout()" [disabled]="!isQuote() || isLaunchingCheckout">{{ isLaunchingCheckout ? 'Processing...' : 'Checkout' }}</button>
                </div>
                @if (saveEstimateValidationErrors().length > 0) {
                  <div class="validation-box">
                    @for (error of saveEstimateValidationErrors(); track error) {
                      <div class="validation-line">{{ error }}</div>
                    }
                  </div>
                }
                <div class="message">{{ invoiceLifecycleMessage() }}</div>
                @if (cartMessage) {
                  <div class="message">{{ cartMessage }}</div>
                }
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { display: flex; flex-direction: column; min-height: 100vh; }
    .page-hero { border-bottom: 1px solid var(--color-border); padding: 2.5rem 1.5rem; background: linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%); }
    .page-hero-inner, .page-body-inner { margin: 0 auto; max-width: 1280px; }
    .home-btn { color: var(--color-muted); text-decoration: none; }
    .hero-top-row { align-items: center; display: flex; gap: 0.85rem; margin-top: 0.7rem; }
    .hero-icon { align-items: center; border-radius: 0.75rem; display: inline-flex; font-size: 1.5rem; height: 52px; justify-content: center; width: 52px; }
    .hero-icon--sky { background: color-mix(in srgb, #0ea5e9 16%, transparent); }
    .page-title { margin: 0.7rem 0 0; }
    .page-sub { color: var(--color-muted); margin: 0.3rem 0 0; }
    .page-body { flex: 1; padding: 2rem 1.5rem; }
    .layout { display: grid; gap: 1.1rem; grid-template-columns: minmax(260px, 25%) 1fr; }
    .left-column { display: grid; gap: 1.1rem; }
    .card { background: color-mix(in srgb, var(--color-surface-2) 75%, transparent); border: 1px solid var(--color-border); border-radius: 1rem; overflow: hidden; }
    .card-hd { align-items: center; border-bottom: 1px solid var(--color-border); display: flex; justify-content: space-between; padding: 0.8rem 1rem; }
    .card-title { font-weight: 700; }
    .card-count { color: var(--color-muted); font-size: 0.8rem; }
    .invoice-count-actions { align-items: center; display: inline-flex; gap: 0.35rem; }
    .invoice-filter-bar { align-items: center; border-bottom: 1px solid var(--color-border); display: flex; gap: 0.55rem; padding: 0.6rem 1rem; }
    .invoice-filter-input { background: color-mix(in srgb, var(--color-surface-2) 60%, transparent); border: 1px solid color-mix(in srgb, var(--color-border) 65%, transparent); border-radius: 0.55rem; color: var(--color-text); flex: 1; font-size: 0.8rem; outline: none; padding: 0.45rem 0.6rem; width: 100%; }
    .invoice-filter-input::placeholder { color: var(--color-muted); }
    .invoice-filter-input:focus { border-color: color-mix(in srgb, #0ea5e9 55%, var(--color-border)); }
    .invoice-filter-clear-btn { background: transparent; border: 1px solid var(--color-border); border-radius: 0.45rem; color: var(--color-muted); cursor: pointer; font-size: 0.75rem; padding: 0.35rem 0.55rem; }
    .invoice-filter-clear-btn:hover { color: var(--color-text); }
    .list { display: grid; }
    .row, .product-row, .cart-row { border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); padding: 0.75rem 1rem; }
    .row { background: transparent; border-left: 0; border-right: 0; border-top: 0; cursor: pointer; display: flex; flex-direction: column; text-align: left; }
    .row--selected { background: color-mix(in srgb, #0ea5e9 10%, transparent); }
    .row-name { color: var(--color-text); font-size: 0.86rem; font-weight: 600; }
    .row-meta { color: var(--color-muted); font-size: 0.75rem; }
    .product-row { align-items: center; display: flex; justify-content: space-between; }
    .product-main { display: flex; flex-direction: column; }
    .product-side { align-items: center; display: flex; gap: 0.7rem; }
    .price { font-size: 0.8rem; font-weight: 600; }
    .btn { background: transparent; border: 1px solid var(--color-border); border-radius: 0.5rem; color: var(--color-text); cursor: pointer; font-size: 0.8rem; padding: 0.42rem 0.7rem; }
    .btn:disabled, .qty-btn:disabled, .remove-btn:disabled { cursor: not-allowed; opacity: 0.55; }
    .btn--primary { background: #0ea5e9; border-color: #0ea5e9; color: #fff; }
    .cart-card { display: flex; flex-direction: column; }
    .estimate-pill { background: color-mix(in srgb, #0ea5e9 16%, transparent); border-radius: 999px; color: #38bdf8; font-size: 0.7rem; font-weight: 700; padding: 0.2rem 0.55rem; }
    .cart-header-panel { border-bottom: 1px solid var(--color-border); display: grid; gap: 0.8rem; padding: 0.9rem 1rem; }
    .cart-header-grid { display: grid; gap: 0.7rem; grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .cart-header-item { display: grid; gap: 0.15rem; }
    .cart-header-item span { color: var(--color-muted); font-size: 0.72rem; }
    .cart-header-item strong { color: var(--color-text); font-size: 0.8rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .cart-contact-panel { border-top: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); display: grid; gap: 0.6rem; padding-top: 0.75rem; }
    .cart-contact-title { font-size: 0.8rem; font-weight: 700; }
    .cart-contact-grid { display: grid; gap: 0.7rem; grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .cart-list { flex: 1; }
    .line-items-header-row, .line-items-data-row { display: grid; grid-template-columns: minmax(0, 1fr) 4.5rem 7rem 7rem 4.75rem; align-items: start; gap: .5rem; padding: 0.75rem 1rem; }
    .line-items-header-row { border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); font-size: 0.82rem; font-weight: 700; }
    .line-items-data-row { border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); }
    .line-items-col-item-header { align-items: center; display: flex; justify-content: flex-start; gap: .3rem; }
    .line-items-col-item { min-width: 0; }
    .line-items-col-qty { text-align: center; }
    .line-items-col-price, .line-items-col-total { text-align: right; }
    .line-items-col-remove { display: flex; justify-content: center; align-items: flex-start; }
    .line-items-add-btn { border: none; background: transparent; color: #86efac; cursor: pointer; font-size: 1.2rem; font-weight: 700; line-height: 1; padding: 0 .1rem; display: inline-flex; align-items: center; justify-content: center; }
    .line-items-add-btn:disabled { cursor: not-allowed; opacity: 0.55; }
    .line-item-name { color: var(--color-text); font-size: 0.86rem; font-weight: 600; }
    .line-item-meta { color: var(--color-muted); font-size: 0.75rem; }
    .qty-controls { align-items: center; display: flex; gap: 0.4rem; justify-content: center; }
    .qty-btn { background: transparent; border: 1px solid var(--color-border); border-radius: 0.35rem; color: var(--color-text); cursor: pointer; height: 26px; width: 26px; }
    .remove-btn { background: transparent; border: 0; color: #f87171; cursor: pointer; font-size: 0.78rem; }
    .cart-footer { border-top: 1px solid var(--color-border); display: grid; gap: 0.7rem; padding: 1rem; }
    .selected-booking { font-size: 0.85rem; }
    .totals-checkout-title { font-size: 0.84rem; font-weight: 700; }
    .totals-box { border: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); border-radius: 0.7rem; display: grid; gap: 0.4rem; padding: 0.65rem 0.75rem; }
    .totals-line { align-items: center; display: flex; font-size: 0.82rem; justify-content: space-between; }
    .totals-line--grand { border-top: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent); margin-top: 0.2rem; padding-top: 0.45rem; }
    .actions { display: flex; gap: 0.6rem; }
    .validation-box { border: 1px solid color-mix(in srgb, #f87171 45%, var(--color-border)); border-radius: 0.6rem; display: grid; gap: 0.3rem; padding: 0.55rem 0.65rem; }
    .validation-line { color: #fca5a5; font-size: 0.76rem; }
    .message { color: var(--color-muted); font-size: 0.78rem; }
    .card-empty { color: var(--color-muted); padding: 1rem; text-align: center; }
    @media (max-width: 1200px) { .cart-header-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 1100px) { .cart-contact-grid { grid-template-columns: 1fr; } }
    @media (max-width: 980px) { .layout { grid-template-columns: 1fr; } .line-items-header-row, .line-items-data-row { grid-template-columns: minmax(0, 1fr) 4.25rem 6rem 6rem 4rem; } }
  `]
})
export class MyCartPageComponent implements OnInit {
  private readonly bookingService = inject(BookingService);
  private readonly productService = inject(ProductService);
  private readonly organizationService = inject(OrganizationService);
  private readonly myCartService = inject(MyCartService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly helcimPayModalService = inject(HelcimPayModalService);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  bookings: BookingDto[] = [];
  products: ProductDto[] = [];
  invoices: InvoiceDto[] = [];
  currentOrganization: OrganizationDto | null = null;
  organizationId: number | null = null;
  estimateId: number | null = null;
  estimateOrganizationId: number | null = null;
  estimateInvoiceNumber = '';
  estimateType = 'ESTIMATE';
  estimateStatus = 'DUE';
  estimateSubtotal = 0;
  estimateDiscount = 0;
  estimateTax = 0;
  estimateShipping = 0;
  estimateAmountDue = 0;
  estimateAmountPaid = 0;
  estimatePickupDate = '';
  estimatePickupName = '';
  estimateContactName = '';
  estimateContactEmail = '';
  estimateContactPhone = '';
  paymentCompletionStatus = '';
  isEstimateDirty = false;
  isLaunchingCheckout = false;

  readonly helcimPayEnabled = environment.helcimPay.enabled;

  readonly selectedBookingId = signal<number | null>(null);
  readonly cartLines = signal<CartLine[]>([]);

  isLoadingBookings = true;
  isLoadingProducts = true;
  isLoadingInvoices = true;
  bookingError = '';
  productError = '';
  invoiceError = '';
  isProductListPopupOpen = false;
  isCreatingInvoice = false;
  invoiceFilterText = '';
  cartMessage = '';

  ngOnInit(): void {
    this.loadOrganization();
    this.loadBookings();
    this.loadProducts();
    this.loadInvoices();
  }

  clearInvoiceFilter(): void {
    this.invoiceFilterText = '';
  }

  invoiceEmptyMessage(): string {
    return this.invoiceFilterText.trim()
      ? `No invoices match filter: "${this.invoiceFilterText.trim()}".`
      : 'No invoices found for this organization.';
  }

  private loadOrganization(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentOrganization = org;
        this.organizationId = org.id;
        this.scheduleViewUpdate();
      },
      error: () => {
        this.currentOrganization = null;
        this.organizationId = null;
        this.cartMessage = 'Unable to resolve organization context.';
        this.scheduleViewUpdate();
      }
    });
  }

  private loadInvoices(): void {
    this.isLoadingInvoices = true;
    this.invoiceError = '';

    this.invoiceService.getAll().subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const dtos = this.coerceArray<InvoiceDto>(response);
          this.invoices = [...dtos].sort((a, b) =>
            new Date(b.dateIssued || b.dateCreated).getTime() - new Date(a.dateIssued || a.dateCreated).getTime()
          );
          this.isLoadingInvoices = false;
          this.scheduleViewUpdate();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          console.error('Failed to load invoices:', err);
          this.invoices = [];
          this.invoiceError = 'Unable to load invoices.';
          this.isLoadingInvoices = false;
          this.scheduleViewUpdate();
        });
      }
    });
  }

  filteredInvoices(): InvoiceDto[] {
    if (!this.organizationId) {
      return [];
    }

    const scopedInvoices = this.invoices.filter((invoice) => invoice.organizationId === this.organizationId);
    const query = this.invoiceFilterText.trim().toLowerCase();

    if (!query) {
      return scopedInvoices;
    }

    return scopedInvoices.filter((invoice) => {
      const issuedDate = invoice.dateIssued || invoice.dateCreated || '';
      const dateText = issuedDate ? this.formatDateTime(issuedDate) : '';
      const searchBlob = [
        issuedDate,
        dateText,
        invoice.status,
        invoice.type,
        invoice.invoiceNumber
      ]
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(query);
    });
  }

  selectInvoice(invoice: InvoiceDto): void {
    this.myCartService.getEstimate(invoice.id, invoice.organizationId).subscribe({
      next: (estimate) => {
        const resolvedBookingId = estimate.bookingId
          ?? this.bookings.find((booking) => booking.invoiceId === invoice.id && booking.organizationId === invoice.organizationId)?.id
          ?? null;
        this.selectedBookingId.set(resolvedBookingId);
        this.estimateId = estimate.estimateId;
        this.estimateOrganizationId = estimate.organizationId;
        this.estimateInvoiceNumber = estimate.invoiceNumber;
        this.estimateType = estimate.type ?? 'INVOICE';
        this.updateEstimateTotals(estimate);
        this.isEstimateDirty = false;
        this.cartLines.set(estimate.items.map((item) => ({
          productId: item.productId,
          name: item.name,
          sku: item.sku,
          unitPrice: item.unitPrice,
          quantity: item.quantity
        })));
        this.cartMessage = `Loaded invoice ${estimate.invoiceNumber}.`;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to load invoice details.';
        this.cartMessage = `Invoice load failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.scheduleViewUpdate();
      }
    });
  }

  private loadBookings(): void {
    this.isLoadingBookings = true;
    this.bookingError = '';

    this.bookingService.getAll().subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const dtos = this.coerceArray<BookingDto>(response);
          this.bookings = [...dtos].sort((a, b) =>
            new Date(b.scheduledDateTime).getTime() - new Date(a.scheduledDateTime).getTime()
          );
          this.isLoadingBookings = false;
          this.scheduleViewUpdate();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          console.error('Failed to load bookings:', err);
          this.bookings = [];
          this.bookingError = 'Unable to load bookings.';
          this.isLoadingBookings = false;
          this.scheduleViewUpdate();
        });
      }
    });
  }

  private loadProducts(): void {
    this.isLoadingProducts = true;
    this.productError = '';

    this.productService.getAll().subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const dtos = this.coerceArray<ProductDto>(response);
          this.products = [...dtos].sort((a, b) => a.name.localeCompare(b.name));
          this.isLoadingProducts = false;
          this.scheduleViewUpdate();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          console.error('Failed to load products:', err);
          this.products = [];
          this.productError = 'Unable to load products.';
          this.isLoadingProducts = false;
          this.scheduleViewUpdate();
        });
      }
    });
  }

  private scheduleViewUpdate(): void {
    queueMicrotask(() => this.changeDetectorRef.detectChanges());
  }

  private updateEstimateTotals(estimate: Partial<MyCartEstimate>): void {
    this.estimateSubtotal = estimate.subtotal ?? 0;
    this.estimateDiscount = estimate.discount ?? 0;
    this.estimateTax = estimate.tax ?? 0;
    this.estimateShipping = estimate.shipping ?? 0;
    this.estimateStatus = estimate.status ?? this.estimateStatus;
    this.estimateAmountDue = estimate.amountDue ?? estimate.subtotal ?? 0;
    this.estimateAmountPaid = estimate.amountPaid ?? 0;
    this.estimatePickupDate = estimate.pickupDate ?? '';
    this.estimatePickupName = estimate.pickupName ?? '';
    this.estimateContactName = estimate.contactName ?? '';
    this.estimateContactEmail = estimate.contactEmail ?? '';
    this.estimateContactPhone = estimate.contactPhone ?? '';
  }

  private clearEstimateTotals(): void {
    this.estimateSubtotal = 0;
    this.estimateDiscount = 0;
    this.estimateTax = 0;
    this.estimateShipping = 0;
    this.estimateStatus = 'DUE';
    this.estimateAmountDue = 0;
    this.estimateAmountPaid = 0;
    this.estimatePickupDate = '';
    this.estimatePickupName = '';
    this.estimateContactName = '';
    this.estimateContactEmail = '';
    this.estimateContactPhone = '';
    this.paymentCompletionStatus = '';
  }

  selectBooking(booking: BookingDto): void {
    if (
      this.estimateId &&
      this.estimateOrganizationId &&
      this.estimateOrganizationId !== booking.organizationId
    ) {
      this.estimateId = null;
      this.estimateOrganizationId = null;
      this.cartMessage = 'Switched organizations. A new estimate will be created for this booking.';
    }

    this.selectedBookingId.set(booking.id);

    if (booking.invoiceId && booking.invoiceId > 0) {
      this.myCartService.getEstimate(booking.invoiceId, booking.organizationId).subscribe({
        next: (estimate) => {
          this.estimateId = estimate.estimateId;
          this.estimateOrganizationId = estimate.organizationId;
          this.estimateInvoiceNumber = estimate.invoiceNumber;
          this.estimateType = estimate.type ?? 'ESTIMATE';
          this.updateEstimateTotals(estimate);
          this.isEstimateDirty = false;
          this.cartLines.set(estimate.items.map((item) => ({
            productId: item.productId,
            name: item.name,
            sku: item.sku,
            unitPrice: item.unitPrice,
            quantity: item.quantity
          })));
          this.cartMessage = `Loaded estimate ${estimate.invoiceNumber}.`;
        },
        error: (err) => {
          this.cartLines.set([]);
          this.estimateId = null;
          this.estimateOrganizationId = null;
          this.estimateInvoiceNumber = '';
          this.clearEstimateTotals();
          this.isEstimateDirty = false;
          const message = err?.error?.message
            ?? err?.error
            ?? err?.message
            ?? 'Unable to load booking estimate.';
          this.cartMessage = `Load failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        }
      });
      return;
    }

    this.cartLines.set([]);
    this.estimateId = null;
    this.estimateOrganizationId = null;
    this.estimateInvoiceNumber = '';
    this.estimateType = 'ESTIMATE';
    this.estimateStatus = 'DUE';
    this.clearEstimateTotals();
    this.isEstimateDirty = false;
    this.cartMessage = 'No existing estimate for selected booking. Cart cleared.';
  }

  addToCart(product: ProductDto): void {
    const unitPrice = product.defaultPrice ?? 0;

    this.cartLines.update((lines) => {
      const existingIndex = lines.findIndex((line) => line.productId === product.id);
      if (existingIndex >= 0) {
        const updated = [...lines];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1
        };
        return updated;
      }

      return [
        ...lines,
        {
          productId: product.id,
          name: product.name,
          sku: product.sku,
          unitPrice,
          quantity: 1
        }
      ];
    });

    this.isEstimateDirty = true;
  }

  incrementQuantity(productId: number): void {
    this.cartLines.update((lines) =>
      lines.map((line) =>
        line.productId === productId
          ? { ...line, quantity: line.quantity + 1 }
          : line
      )
    );

    this.isEstimateDirty = true;
  }

  decrementQuantity(productId: number): void {
    this.cartLines.update((lines) =>
      lines
        .map((line) =>
          line.productId === productId
            ? { ...line, quantity: line.quantity - 1 }
            : line
        )
        .filter((line) => line.quantity > 0)
    );

    this.isEstimateDirty = true;
  }

  removeLine(productId: number): void {
    this.cartLines.update((lines) => lines.filter((line) => line.productId !== productId));
    this.isEstimateDirty = true;
  }

  openProductListPopup(): void {
    this.isProductListPopupOpen = true;
  }

  closeProductListPopup(): void {
    this.isProductListPopupOpen = false;
  }

  addProductFromPopup(product: ProductDto): void {
    this.addToCart(product);
    this.closeProductListPopup();
  }

  createInvoice(): void {
    if (!this.organizationId) {
      this.cartMessage = 'Organization context is required to create an invoice.';
      return;
    }

    this.isCreatingInvoice = true;

    this.invoiceService.create({
      organizationId: this.organizationId,
      invoiceNumber: '',
      currency: 'USD',
      pickupDate: new Date().toISOString(),
      type: 'ESTIMATE',
      totalTaxAmount: 0,
      totalDiscountAmount: 0,
      amount: 0,
      amountPaid: 0,
      amountDue: 0,
      shippingAmount: 0,
      depositAmount: 0,
      convenienceFee: 0,
      convenienceFeeEnabled: false,
      status: 'DUE',
      items: []
    }).subscribe({
      next: (createdInvoice) => {
        this.invoices = [createdInvoice, ...this.invoices.filter((invoice) => invoice.id !== createdInvoice.id)].sort((a, b) =>
          new Date(b.dateIssued || b.dateCreated).getTime() - new Date(a.dateIssued || a.dateCreated).getTime()
        );
        this.selectInvoice(createdInvoice);
        this.cartMessage = `Invoice ${createdInvoice.invoiceNumber || 'created'} added to cart.`;
        this.isCreatingInvoice = false;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to create invoice.';
        this.cartMessage = `Create invoice failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.isCreatingInvoice = false;
        this.scheduleViewUpdate();
      }
    });
  }

  saveEstimate(): void {
    const validationErrors = this.saveEstimateValidationErrors();
    if (validationErrors.length > 0) {
      this.cartMessage = validationErrors[0];
      return;
    }

    const selectedBooking = this.getSelectedBooking();

    if (!selectedBooking) {
      this.cartMessage = 'Select a booking before saving an estimate.';
      return;
    }

    const requestOrganizationId = selectedBooking.organizationId ?? this.organizationId;

    if (
      this.estimateId &&
      this.estimateOrganizationId &&
      requestOrganizationId &&
      this.estimateOrganizationId !== requestOrganizationId
    ) {
      this.estimateId = null;
      this.estimateOrganizationId = null;
    }

    if (!requestOrganizationId) {
      this.cartMessage = 'Organization context is required to save estimate.';
      return;
    }

    if (this.cartLines().length === 0) {
      this.cartMessage = 'Add at least one product to save an estimate.';
      return;
    }

    this.myCartService.saveEstimate({
      organizationId: requestOrganizationId,
      estimateId: this.estimateId ?? undefined,
      bookingId: selectedBooking?.id,
      items: this.cartLines().map((line) => ({
        productId: line.productId,
        quantity: line.quantity
      }))
    }).subscribe({
      next: (result) => {
        this.estimateId = result.estimateId;
        this.estimateOrganizationId = result.organizationId;
        this.estimateInvoiceNumber = result.invoiceNumber;
        this.estimateType = result.type ?? 'ESTIMATE';
        this.updateEstimateTotals(result);
        this.isEstimateDirty = false;
        this.cartMessage = `Estimate ${result.invoiceNumber} saved.`;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to save estimate.';
        this.cartMessage = `Save failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.scheduleViewUpdate();
      }
    });
  }

  saveEstimateValidationErrors(): string[] {
    const errors: string[] = [];
    const selectedBooking = this.getSelectedBooking();

    if (!selectedBooking) {
      errors.push('Select a booking before saving an estimate.');
    }

    if (!this.organizationId) {
      errors.push('Organization context is required to save estimate.');
    }

    if (this.cartLines().length === 0) {
      errors.push('Add at least one product to save an estimate.');
    }

    if (!this.resolvePickupDateForValidation()) {
      errors.push('Pickup date is required before saving an estimate.');
    }

    if (!this.resolveContactNameForValidation()) {
      errors.push('Customer contact name is required before saving an estimate.');
    }

    if (!this.resolveContactEmailForValidation()) {
      errors.push('Customer contact email is required before saving an estimate.');
    }

    if (!this.resolveContactPhoneForValidation()) {
      errors.push('Customer contact phone is required before saving an estimate.');
    }

    return errors;
  }

  saveQuote(): void {
    if (!this.estimateId) {
      this.cartMessage = 'Save estimate before creating a quote.';
      return;
    }

    if (!this.selectedBookingId()) {
      this.cartMessage = 'Select a booking before saving a quote.';
      return;
    }

    const selectedBooking = this.getSelectedBooking();
    const requestOrganizationId = selectedBooking?.organizationId ?? this.estimateOrganizationId ?? this.organizationId;
    if (!requestOrganizationId) {
      this.cartMessage = 'Organization context is required to save quote.';
      return;
    }

    this.myCartService.saveQuote({
      organizationId: requestOrganizationId,
      estimateId: this.estimateId,
      bookingId: this.selectedBookingId()!
    }).subscribe({
      next: (result) => {
        this.estimateInvoiceNumber = result.invoiceNumber;
        this.estimateType = result.type ?? 'QUOTE';
        this.updateEstimateTotals(result);
        this.isEstimateDirty = false;
        this.cartMessage = `Quote ${result.invoiceNumber} saved.`;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to save quote.';
        this.cartMessage = `Save quote failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.scheduleViewUpdate();
      }
    });
  }

  changeQuote(): void {
    if (!this.estimateId) {
      this.cartMessage = 'No quote selected.';
      return;
    }

    if (!this.selectedBookingId()) {
      this.cartMessage = 'Select a booking before changing quote.';
      return;
    }

    const selectedBooking = this.getSelectedBooking();
    const requestOrganizationId = selectedBooking?.organizationId ?? this.estimateOrganizationId ?? this.organizationId;
    if (!requestOrganizationId) {
      this.cartMessage = 'Organization context is required to change quote.';
      return;
    }

    this.myCartService.changeQuote({
      organizationId: requestOrganizationId,
      estimateId: this.estimateId,
      bookingId: this.selectedBookingId()!
    }).subscribe({
      next: (result) => {
        this.estimateInvoiceNumber = result.invoiceNumber;
        this.estimateType = result.type ?? 'ESTIMATE';
        this.updateEstimateTotals(result);
        this.isEstimateDirty = false;
        this.cartMessage = `Quote changed to ${this.estimateType}.`;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to change quote.';
        this.cartMessage = `Change quote failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.scheduleViewUpdate();
      }
    });
  }

  createOrder(): void {
    if (!this.estimateId) {
      this.cartMessage = 'No quote selected.';
      return;
    }

    if (!this.selectedBookingId()) {
      this.cartMessage = 'Select a booking before creating order.';
      return;
    }

    const selectedBooking = this.getSelectedBooking();
    const requestOrganizationId = selectedBooking?.organizationId ?? this.estimateOrganizationId ?? this.organizationId;
    if (!requestOrganizationId) {
      this.cartMessage = 'Organization context is required to create order.';
      return;
    }

    this.myCartService.createOrder({
      organizationId: requestOrganizationId,
      estimateId: this.estimateId,
      bookingId: this.selectedBookingId()!
    }).subscribe({
      next: (result) => {
        this.estimateInvoiceNumber = result.invoiceNumber;
        this.estimateType = result.type ?? 'ORDER';
        this.updateEstimateTotals(result);
        this.isEstimateDirty = false;
        this.cartMessage = `Order ${result.invoiceNumber} created.`;
        this.scheduleViewUpdate();
      },
      error: (err) => {
        const message = err?.error?.message
          ?? err?.error
          ?? err?.message
          ?? 'Unable to create order.';
        this.cartMessage = `Order failed (${err?.status ?? 'unknown'}): ${String(message)}`;
        this.scheduleViewUpdate();
      }
    });
  }

  async checkout(): Promise<void> {
    if (!this.estimateId) {
      this.cartMessage = 'Save estimate before checkout.';
      return;
    }

    if (this.cartLines().length === 0) {
      this.cartMessage = 'Add at least one product before checkout.';
      return;
    }

    if (!this.selectedBookingId()) {
      this.cartMessage = 'Select a booking before final checkout.';
      return;
    }

    const selectedBooking = this.getSelectedBooking();
    const requestOrganizationId = selectedBooking?.organizationId ?? this.estimateOrganizationId ?? this.organizationId;
    if (!requestOrganizationId) {
      this.cartMessage = 'Organization context is required for checkout.';
      return;
    }

    const amountToCharge = Number(this.displayAmountDue().toFixed(2));
    if (amountToCharge <= 0) {
      this.cartMessage = 'No balance due for checkout.';
      return;
    }

    if (!this.helcimPayEnabled) {
      this.cartMessage = 'HelcimPay modal is disabled for this environment.';
      return;
    }

    this.isLaunchingCheckout = true;

    try {
      const lifecycleResult = await firstValueFrom(this.myCartService.checkout({
        organizationId: requestOrganizationId,
        estimateId: this.estimateId,
        bookingId: this.selectedBookingId()!
      }));

      this.estimateInvoiceNumber = lifecycleResult.invoiceNumber;
      this.estimateType = lifecycleResult.type ?? 'INVOICE';
      this.updateEstimateTotals(lifecycleResult);
      this.isEstimateDirty = false;

      const initResponse = await firstValueFrom(this.testPaymentService.initializeCheckout({
        organizationId: requestOrganizationId,
        amount: amountToCharge,
        currency: 'USD',
        paymentType: 'purchase',
        customerCode: requestOrganizationId.toString(),
        invoiceNumber: this.estimateInvoiceNumber || undefined
      }));

      const modalPayload = await this.helcimPayModalService.openModal(initResponse.checkoutToken);
      if (!modalPayload.transactionId || !modalPayload.hash) {
        throw new Error('HelcimPay modal response is missing required transaction data.');
      }

      const paidAmount = modalPayload.amount ?? amountToCharge;
      const paymentStatus = modalPayload.status?.trim() || 'Approved';
      const completeResponse = await firstValueFrom(this.testPaymentService.completeCheckout({
        organizationId: requestOrganizationId,
        checkoutSessionId: initResponse.checkoutSessionId,
        helcimPayloadJson: modalPayload.innerDataJson ?? extractInnerDataJson(modalPayload.raw) ?? '',
        transactionId: modalPayload.transactionId,
        amount: paidAmount,
        currency: modalPayload.currency?.trim().toUpperCase() || 'USD',
        cardToken: modalPayload.cardToken,
        hash: modalPayload.hash,
        status: paymentStatus,
        approvalCode: getFieldFromPayload(modalPayload.raw, ['approvalCode', 'approval_code', 'authCode', 'auth_code']),
        dateCreated: getFieldFromPayload(modalPayload.raw, ['dateCreated', 'date_created', 'createdAt', 'created_at']),
        customerCode: getFieldFromPayload(modalPayload.raw, ['customerCode', 'customer_code']) || requestOrganizationId.toString(),
        invoiceNumber: this.estimateInvoiceNumber || undefined
      }));

      this.paymentCompletionStatus = completeResponse.status || paymentStatus;
      this.estimateAmountPaid = Math.max(this.estimateAmountPaid, paidAmount);
      this.estimateAmountDue = Math.max(0, this.estimateAmountDue - paidAmount);
      this.cartMessage = `Checkout complete. Invoice ${this.estimateInvoiceNumber} is now ${this.estimateType}.`;
    } catch (err: unknown) {
      const error = err as { error?: { message?: string } | string; message?: string; status?: number };
      const message = error?.error && typeof error.error === 'object'
        ? error.error.message
        : error?.error
          ?? error?.message
          ?? 'Unable to checkout estimate.';
      this.cartMessage = `Checkout failed (${error?.status ?? 'unknown'}): ${String(message)}`;
    } finally {
      this.isLaunchingCheckout = false;
      this.scheduleViewUpdate();
    }
  }

  lineTotal(line: CartLine): number {
    return line.unitPrice * line.quantity;
  }

  cartTotal(): number {
    return this.cartLines().reduce((sum, line) => sum + this.lineTotal(line), 0);
  }

  displaySubtotal(): number {
    const computedSubtotal = this.cartTotal();
    return computedSubtotal > 0 ? computedSubtotal : this.estimateSubtotal;
  }

  displayAmountDue(): number {
    if (this.isEstimateDueState() || this.isQuoteDueState()) {
      return Math.max(0, this.displaySubtotal() - this.appliedPayments());
    }

    if (this.isQuoteInProgressState()) {
      return Math.max(0, this.displaySubtotal() + this.displayTax() + this.displayShipping() - this.displayDiscount());
    }

    if (this.isInvoiceDueState() || this.isInvoicePaidState()) {
      return this.estimateAmountDue;
    }

    return Math.max(0, this.estimateAmountDue ?? this.displaySubtotal());
  }

  displayAmountPaid(): number {
    if (this.isEstimateDueState() || this.isQuoteDueState()) {
      return 0;
    }

    return this.estimateAmountPaid;
  }

  displayAmountPaidText(): string {
    const amount = this.formatCurrency(this.displayAmountPaid());
    const status = this.paymentCompletionStatus?.trim();
    return status ? `(${status}) ${amount}` : amount;
  }

  displayDiscount(): number {
    return this.isEstimateState() || this.isQuoteDueState()
      ? 0
      : this.estimateDiscount;
  }

  displayTax(): number {
    return this.isEstimateState() || this.isQuoteDueState()
      ? 0
      : this.estimateTax;
  }

  displayShipping(): number {
    return this.isEstimateState() || this.isQuoteDueState()
      ? 0
      : this.estimateShipping;
  }

  displayAdjustmentText(value: number): string {
    if (this.isEstimateDueState()) {
      return `TBD on checkout ${this.formatCurrency(value)}`;
    }

    if (this.isQuoteDueState()) {
      return `Calculating ${this.formatCurrency(value)}`;
    }

    return this.formatCurrency(value);
  }

  invoiceLifecycleMessage(): string {
    const invoiceNumber = this.estimateInvoiceNumber || 'Pending';
    const type = (this.estimateType || '').toUpperCase();
    const status = (this.estimateStatus || '').toUpperCase();

    if (type === 'ESTIMATE' && status === 'DUE') {
      return `This is an estimate for ${invoiceNumber}. Changes can be made to the cart before the quote is generated.`;
    }

    if (type === 'QUOTE' && status === 'DUE') {
      return `This is a quote in progress for ${invoiceNumber}. Taxes, shipping, and discounts are being calculated and will be available soon.`;
    }

    if (type === 'QUOTE' && status === 'IN_PROGRESS') {
      return `This quote is valid for 3 days for ${invoiceNumber}. Any changes require restarting the quote or proceeding to checkout.`;
    }

    if (type === 'INVOICE' && status === 'DUE') {
      return `This is an invoice for ${invoiceNumber}. The status of this invoice is ${this.displayInvoiceStatus()}.`;
    }

      if (type === 'INVOICE' && status === 'PAID') {
          return `This invoice ${invoiceNumber} has been paid. Thank you for your business!`;
    }


    return `This is an invoice for ${invoiceNumber}. The status of this invoice is ${this.displayInvoiceStatus()}.`;
  }

  formatDateTime(value: string): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  private coerceArray<T>(value: unknown): T[] {
    if (typeof value === 'string') {
      try {
        return this.coerceArray<T>(JSON.parse(value));
      } catch {
        return [];
      }
    }

    if (Array.isArray(value)) {
      return value as T[];
    }

    if (value && typeof value === 'object') {
      const candidate = value as { $values?: unknown; items?: unknown; value?: unknown; data?: unknown };
      if (Array.isArray(candidate.$values)) {
        return candidate.$values as T[];
      }

      if (Array.isArray(candidate.items)) {
        return candidate.items as T[];
      }

      if (Array.isArray(candidate.value)) {
        return candidate.value as T[];
      }

      if (Array.isArray(candidate.data)) {
        return candidate.data as T[];
      }
    }

    return [];
  }

  private getSelectedBooking(): BookingDto | null {
    const bookingId = this.selectedBookingId();
    if (!bookingId) {
      return null;
    }

    return this.bookings.find((booking) => booking.id === bookingId) ?? null;
  }

  selectedBookingSummary(): string {
    const booking = this.getSelectedBooking();
    if (!booking) {
      return 'None selected';
    }

    return `${booking.name} · ${this.formatDateTime(booking.scheduledDateTime)}`;
  }

  selectedPickupDate(): string {
    if (!this.estimatePickupDate) {
      return 'Not selected';
    }

    return this.formatDateTime(this.estimatePickupDate);
  }

  selectedPickupName(): string {
    return this.estimatePickupName || 'Not available';
  }

  customerContactName(): string {
    return this.estimateContactName || 'Not available';
  }

  customerContactEmail(): string {
    return this.estimateContactEmail || 'Not available';
  }

  customerContactPhone(): string {
    return this.estimateContactPhone || 'Not available';
  }

  private resolvePickupDateForValidation(): string {
    return (this.estimatePickupDate || this.getSelectedBooking()?.scheduledDateTime || '').trim();
  }

  private resolveContactNameForValidation(): string {
    return (this.estimateContactName || this.currentOrganization?.contactName || this.currentOrganization?.billingContactName || '').trim();
  }

  private resolveContactEmailForValidation(): string {
    return (this.estimateContactEmail || this.currentOrganization?.contactEmail || this.currentOrganization?.billingContactEmail || '').trim();
  }

  private resolveContactPhoneForValidation(): string {
    return (this.estimateContactPhone || this.currentOrganization?.billingContactPhone || this.currentOrganization?.shippingContactPhone || '').trim();
  }

  displayInvoiceStatus(): string {
    const status = (this.estimateStatus || '').trim();
    return !status || status.toUpperCase() === 'UNKNOWN'
      ? 'Status Unknown'
      : status;
  }

  isEstimate(): boolean {
    return (this.estimateType || '').toUpperCase() === 'ESTIMATE';
  }

  isQuote(): boolean {
    return (this.estimateType || '').toUpperCase() === 'QUOTE';
  }

  isQuoteOrLater(): boolean {
    const normalized = (this.estimateType || '').toUpperCase();
    return normalized === 'QUOTE' || normalized === 'INVOICE';
  }

  private isEstimateState(): boolean {
    return this.isEstimate() || this.isEstimateDirty;
  }

  private isEstimateDueState(): boolean {
    const normalizedType = (this.estimateType || '').toUpperCase();
    const normalizedStatus = (this.estimateStatus || '').toUpperCase();
    return (normalizedType === 'ESTIMATE' && normalizedStatus === 'DUE') || this.isEstimateDirty;
  }

  private isQuoteDueState(): boolean {
    const normalizedType = (this.estimateType || '').toUpperCase();
    const normalizedStatus = (this.estimateStatus || '').toUpperCase();
    return normalizedType === 'QUOTE' && normalizedStatus === 'DUE';
  }

  private isQuoteInProgressState(): boolean {
    const normalizedType = (this.estimateType || '').toUpperCase();
    const normalizedStatus = (this.estimateStatus || '').toUpperCase();
    return normalizedType === 'QUOTE' && normalizedStatus === 'IN_PROGRESS';
  }

  private isInvoiceDueState(): boolean {
    const normalizedType = (this.estimateType || '').toUpperCase();
    const normalizedStatus = (this.estimateStatus || '').toUpperCase();
    return normalizedType === 'INVOICE' && normalizedStatus === 'DUE';
  }

  private isInvoicePaidState(): boolean {
    const normalizedType = (this.estimateType || '').toUpperCase();
    const normalizedStatus = (this.estimateStatus || '').toUpperCase();
    return normalizedType === 'INVOICE' && normalizedStatus === 'PAID';
  }

  private appliedPayments(): number {
    return Math.max(0, this.estimateAmountPaid || 0);
  }
}

function extractInnerDataJson(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const queue: unknown[] = [payload];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(item);
      }

      continue;
    }

    const record = current as Record<string, unknown>;
    const eventMessage = record['eventMessage'];

    if (typeof eventMessage === 'string') {
      const trimmed = eventMessage.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed) as Record<string, unknown>;
          const dataNode = parsed['data'];
          if (dataNode && typeof dataNode === 'object') {
            const innerData = (dataNode as Record<string, unknown>)['data'];
            if (innerData && typeof innerData === 'object' && !Array.isArray(innerData)) {
              return JSON.stringify(innerData);
            }
          }

          queue.push(parsed);
        } catch {
          // ignore invalid JSON payload
        }
      }
    }

    for (const value of Object.values(record)) {
      queue.push(value);
    }
  }

  return undefined;
}

function getFieldFromPayload(payload: unknown, keys: string[]): string | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const queue: unknown[] = [payload];
  const visited = new Set<unknown>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (Array.isArray(current)) {
      for (const item of current) {
        queue.push(item);
      }
      continue;
    }

    const record = current as Record<string, unknown>;
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    for (const value of Object.values(record)) {
      queue.push(value);
    }
  }

  return undefined;
}
